import React from 'react';
import { AlertTriangle, PhoneCall } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface LastContact {
    at: string;
    minutes_ago: number;
    who: string | null;
    by_me: boolean;
    channel: 'call' | 'disposition';
    call_status: string | null;
    outcome: string | null;
    outcome_at?: string | null;
    outcome_by?: string | null;
    order_name: string | null;
    work_type: string | null;
    recent_by_other: boolean;
}

const WORK_LABEL: Record<string, string> = { rto: 'RTO', edd: 'EDD', ndr: 'Failed Delivery' };

function ago(minutes: number): string {
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const h = Math.floor(minutes / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d === 1 ? '' : 's'} ago`;
}

function shortDate(iso: string): string {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * Who last contacted this customer, across every team — Sales, Retention, EDD, NDR,
 * RTO. Amber when someone else reached them in the last 2 hours, so a second agent
 * doesn't call a customer who just spoke to the CRM about another order.
 */
const LastContactStrip: React.FC<{ contact: LastContact | null }> = ({ contact }) => {
    if (!contact) return null;
    const who = contact.by_me ? 'You' : contact.who || 'An agent';
    const what = contact.channel === 'call'
        ? `call${contact.call_status ? ` (${contact.call_status})` : ''}`
        : 'dispositioned';
    const about = [
        contact.work_type ? WORK_LABEL[contact.work_type] ?? contact.work_type : null,
        contact.order_name,
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cn(
                'px-4 py-1.5 border-b text-[12px] flex items-center gap-2 flex-wrap',
                contact.recent_by_other
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
            )}
        >
            {contact.recent_by_other ? (
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600" />
            ) : (
                <PhoneCall className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            )}
            <span className="font-semibold">Last contact:</span>
            <span>
                {who} · {what} · {ago(contact.minutes_ago)}
                {about && <> · {about}</>}
            </span>
            {contact.outcome && contact.channel === 'disposition' && (
                <span className="text-gray-500">· Outcome: {contact.outcome}</span>
            )}
            {contact.outcome && contact.channel === 'call' && (
                <span className="text-gray-500">
                    · Last outcome: {contact.outcome}
                    {(contact.outcome_by || contact.outcome_at) && (
                        <> ({[contact.outcome_by, contact.outcome_at ? shortDate(contact.outcome_at) : null].filter(Boolean).join(', ')})</>
                    )}
                </span>
            )}
            {contact.recent_by_other && (
                <span className="font-semibold">— check before calling again</span>
            )}
        </div>
    );
};

export default LastContactStrip;
